// Union two "Apply" tracking states (local + cloud) without losing anything.
//
// applyData shape:
//   {
//     blocklist: ["Acme", ...],            // companies hidden forever
//     tracked: {
//       "Acme": { applied, lastApplied, coldDm, notes }
//     },
//     custom: [                            // companies the user added themselves
//       { name: "Acme", url: "https://acme.com/careers" }
//     ]
//   }
//
// Merge rules (symmetric, non-destructive):
//   blocklist  -> set union
//   applied    -> OR (once applied somewhere, stays applied)
//   coldDm     -> OR
//   lastApplied-> the later (max) ISO date
//   notes      -> the longer string ("don't lose what I wrote")
//   custom     -> union by (case-insensitive) name, preferring an entry that has a url

// Coerce the user-added company list into a clean [{ name, url }] array,
// dropping malformed entries and de-duplicating by case-insensitive name.
function normalizeCustom(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  list.forEach((c) => {
    if (!c || typeof c !== "object") return;
    const name = typeof c.name === "string" ? c.name.trim() : "";
    if (!name) return;
    const key = name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ name, url: typeof c.url === "string" ? c.url.trim() : "" });
  });
  return out;
}

export function normalizeApply(a) {
  a = a && typeof a === "object" ? a : {};
  const blocklist = Array.isArray(a.blocklist) ? a.blocklist.filter((x) => typeof x === "string") : [];
  const tracked = a.tracked && typeof a.tracked === "object" ? a.tracked : {};
  const out = {};
  Object.keys(tracked).forEach((k) => {
    const t = tracked[k] || {};
    out[k] = {
      applied: !!t.applied,
      lastApplied: t.lastApplied || null,
      coldDm: !!t.coldDm,
      notes: typeof t.notes === "string" ? t.notes : "",
    };
  });
  return { blocklist, tracked: out, custom: normalizeCustom(a.custom) };
}

function maxDate(a, b) {
  if (a && b) return a >= b ? a : b; // ISO yyyy-mm-dd compares lexicographically
  return a || b || null;
}

function pickLonger(a, b) {
  a = a || "";
  b = b || "";
  if (a && b) return a.length >= b.length ? a : b;
  return a || b;
}

// True when an entry carries no real signal — used to prune noise so the synced
// blob doesn't grow forever as companies are toggled on then off.
function isEmptyEntry(t) {
  return !t.applied && !t.coldDm && !t.lastApplied && !(t.notes && t.notes.trim());
}

export function applyMerge(a, b) {
  const x = normalizeApply(a);
  const y = normalizeApply(b);

  const blocklist = Array.from(new Set([...x.blocklist, ...y.blocklist]));

  const tracked = {};
  const names = new Set([...Object.keys(x.tracked), ...Object.keys(y.tracked)]);
  names.forEach((n) => {
    const p = x.tracked[n] || {};
    const q = y.tracked[n] || {};
    const merged = {
      applied: !!p.applied || !!q.applied,
      coldDm: !!p.coldDm || !!q.coldDm,
      lastApplied: maxDate(p.lastApplied, q.lastApplied),
      notes: pickLonger(p.notes, q.notes),
    };
    if (!isEmptyEntry(merged)) tracked[n] = merged;
  });

  // Union the user-added companies by case-insensitive name; if the same name
  // exists on both sides, keep whichever entry actually carries a url.
  const customByKey = new Map();
  [...x.custom, ...y.custom].forEach((c) => {
    const key = c.name.toLowerCase();
    const existing = customByKey.get(key);
    if (!existing || (!existing.url && c.url)) customByKey.set(key, c);
  });
  const custom = Array.from(customByKey.values());

  return { blocklist, tracked, custom };
}

export default applyMerge;
